import { hashWords } from "daedalus-core";
import { ZoneRule } from "../zones/zones.ts";
import { AUTHORING_CELL, canStand, type CollisionGrid } from "../movement/step.ts";
import { feetInside } from "../movement/nearest.ts";

/**
 * Where a crowd arriving at one place stands (ADR-096 mass cases, ADR-137 decision 17): a standable cell of the place's
 * reach, chosen by the arriver's handle and the place's index through the shared seeded hash, so fifty characters
 * standing up at one shrine take different cells and one handle takes the same cell every time. The shrine respawn, a
 * travel arrival and a waypoint arrival share it. Pure and allocation-free: the cells are a preallocated list and the
 * answer is written into a caller's object. The seed of the arrival hash is `SPREAD_SEED`.
 */
export const SPREAD_SEED = 0x5a3e_91c7;

const SCRATCH = new Uint32Array(2);

/**
 * What the spread reads of the zone grid: the rule bits at a feet point.
 */
export interface ZoneRules {
  rulesAt(x: number, y: number): number;
}

/**
 * Where a character stands: a feet point in sixteenths.
 */
export interface SpreadSpot {
  x: number;
  y: number;
}

/**
 * Chooses the cell an arriving character stands on. The hash picks a first cell of the reach and the search walks the
 * list from there, wrapping once, to the first cell whose feet point lies inside the grid, can be stood on and, when
 * `noPvp` is set, lies in a zone that carries `NoPvp`. A reach with one such cell therefore stands everyone on it.
 *
 * @param cells The reach's authoring cells as column and row pairs, `[column0, row0, column1, row1, ...]`.
 * @param handle The arriver's entity handle.
 * @param place The index of the shrine or waypoint it arrives at.
 * @param live The live collision grid.
 * @param zones The zone grid.
 * @param noPvp Whether the cell must shelter from player harm (a shrine does; a waypoint in the open may not).
 * @param out Receives the feet point when a cell is found.
 * @returns True when a cell was found; false for an empty reach or one with no cell that qualifies, in which case the
 *   caller stands the character at the place's own standing spot.
 */
export function spreadSpot(cells: Int32Array, handle: number, place: number, live: CollisionGrid, zones: ZoneRules, noPvp: boolean, out: SpreadSpot): boolean {
  const count = cells.length >>> 1;
  if (count === 0) {
    return false;
  }
  SCRATCH[0] = handle >>> 0;
  SCRATCH[1] = place >>> 0;
  const first = hashWords(SPREAD_SEED, SCRATCH, 2) % count;
  const half = AUTHORING_CELL >>> 1;
  for (let step = 0; step < count; step += 1) {
    const cell = (first + step) % count;
    const x = (cells[cell * 2] as number) * AUTHORING_CELL + half;
    const y = ((cells[cell * 2 + 1] as number) + 1) * AUTHORING_CELL;
    if (!feetInside(x, y, live) || !canStand(x, y, live)) {
      continue;
    }
    if (noPvp && (zones.rulesAt(x, y) & ZoneRule.NoPvp) === 0) {
      continue;
    }
    out.x = x;
    out.y = y;
    return true;
  }
  return false;
}

/**
 * Whether any cell of a reach, at the feet point `spreadSpot` would stand an arrival on, carries a zone rule: travel
 * refuses an Outlaw a place any of whose arrival cells is Sanctuary (ADR-137 decision 16). Nothing is allocated.
 *
 * @param cells The reach's cells, as `spreadSpot` reads them.
 * @param zones The zone grid.
 * @param rule The rule bits, any of which counts.
 * @returns True when a cell carries one.
 */
export function reachHolds(cells: Int32Array, zones: Pick<ZoneRules, "rulesAt">, rule: number): boolean {
  const half = AUTHORING_CELL >>> 1;
  for (let cell = 0; cell < cells.length >>> 1; cell += 1) {
    if ((zones.rulesAt((cells[cell * 2] as number) * AUTHORING_CELL + half, ((cells[cell * 2 + 1] as number) + 1) * AUTHORING_CELL) & rule) !== 0) {
      return true;
    }
  }
  return false;
}
